---
title: Data and storage
description: What data exists, where each kind lives, and why analytics uses ClickHouse.
---

The system produces four kinds of data, and each has one right home:

| Data              | Shape                            | Lives in                          |
| ----------------- | -------------------------------- | --------------------------------- |
| App state         | Accounts, store settings, zones  | Postgres (gateway)                |
| Raw video         | Large immutable files, per night | Object store, pruned on the box   |
| Tracking stream   | Per-frame messages, append-only  | Kafka, transient                  |
| Analytics results | Nightly rows, queried for years  | ClickHouse, plus report artifacts |

<StorageDiagram />

## One night, piece by piece

Follow a single night for store `store-001` on 2026-08-18 and every piece of
data it produces:

1. **Recordings land in the bucket.** One file per camera, plus each
   camera's stream details:

   ```
   retail-sessions/store-001/2026-08-18/videos/cam-01.mp4
   retail-sessions/store-001/2026-08-18/camInfo/cam-01.yml
   ```

2. **Fetch stages the night locally.** The GPU machine copies those files to
   its own disk. This staged folder is the immutable input: every later
   re-run of this night starts from it, byte for byte.

3. **Perception writes the tracking stream.** As DeepStream processes the
   night, every frame's result, who was where in floor-plan meters, is
   published to the Kafka topic. This is the largest derived data of the
   night and it is deliberately transient.

4. **Analytics reads the stream and writes the results.** The report
   (`report.json`) holds the night's numbers and quality flags. Nightly rows
   land in ClickHouse so dashboards can query across nights and stores, and
   the detailed track table is written once as a Parquet artifact next to
   the report.

5. **Cleanup.** Staged video older than the configured keep window (three
   nights by default) is deleted from the machine. The bucket copy, the
   report, the Parquet artifact and the ClickHouse rows all remain.

Two properties fall out of this shape. Any night can be **re-analyzed
without the GPU** while its Kafka messages are retained, because analytics
only reads the topic. And any night can be **fully re-processed** from its
staged or bucket copy when settings improve, because the inputs are
immutable files.

## The question: TimescaleDB, ClickHouse or Parquet?

For the analytics results, three candidates come up. They are not really
competitors; they are different kinds of thing.

### What the workload actually is

Nightly results are **append-only and immutable**: a night is computed once
and never updated. Queries are **analytical**: sums and averages over hours,
days and months, sliced by store, zone and camera. There are **no
transactions** and no row-level updates. Volume grows with stores times
nights, and per-frame tracking data is large but cold.

That profile is the textbook OLAP shape.

### ClickHouse: the serving database

ClickHouse is a columnar database built for exactly this: fast aggregation
over long time ranges of append-only data.

- Columnar storage and aggressive compression make per-hour and per-zone
  rollups over months of nights cheap.
- Cross-store questions ("compare dwell in all stores last quarter") are one
  query, not a fan-out over report files.
- Its Kafka table engine can consume the existing tracking topic directly if
  per-frame data ever needs to be queryable, with no new plumbing.
- It is already provisioned in this stack, next to Kafka on the vision
  profile.

**This is the right serving layer for analytics.** Its known weaknesses,
updates and deletes and transactions, are things this workload never does.

### Parquet: the artifact format, not a database

Parquet is a file format. It cannot serve a dashboard by itself, but it is
the right way to **keep** the detailed outputs:

- Each night's full track table written once as Parquet next to the report,
  in the object store, costs almost nothing and compresses well.
- When an engineer tunes the tracker weeks later, DuckDB reads those files
  ad hoc, without any database in the loop.
- If ClickHouse is ever rebuilt, the Parquet artifacts are the replayable
  source of truth.

**Keep Parquet as the per-night artifact and archive format.** It
complements ClickHouse; it does not replace it.

### TimescaleDB: the one to skip here

TimescaleDB is Postgres with time-series features. Its appeal is operational:
the stack already runs Postgres, and one database engine is simpler than two.
That argument matters at small scale. But:

- The gateway's Postgres is deliberately small and transactional. Pointing
  heavy analytical scans at it couples the app's health to dashboard load,
  so a separate database would be needed anyway.
- Once a separate analytical database is inevitable, a row store with
  time-series extensions is simply a weaker OLAP engine than a native
  columnar one, at every volume that matters here.
- The per-frame data path (Kafka in, columnar out) has first-class support
  in ClickHouse and none in Timescale.

**TimescaleDB would only win if the fleet stayed at a handful of stores and
the team refused to operate a second database engine.** This stack already
operates ClickHouse, so that discount never applies.

### The rule of thumb

> Kafka carries the night. ClickHouse serves the questions. Parquet remembers
> the nights. Postgres stays operational.

## Data lifecycles

| Data                    | Kept for                            | Why that long                                     |
| ----------------------- | ----------------------------------- | ------------------------------------------------- |
| Staged video on the box | 3 nights by default (`keep_nights`) | Enough to re-run recent nights; disk is finite    |
| Video in the bucket     | The client's own retention          | The system never deletes a client's footage       |
| Kafka messages          | 7 days                              | Re-run analytics without touching the GPU         |
| `report.json` + Parquet | Indefinitely                        | Small, and the replayable record of every night   |
| ClickHouse rows         | Indefinitely                        | The queryable history dashboards read             |
| Postgres                | Current state only                  | Operational data, backed up like any app database |
